import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { TenantSummary } from '../../services/api'
import RevenueAnalytics from './RevenueAnalytics'
import BillingWorkflow from './BillingWorkflow'
import FiCockpit from './fi/FiCockpit'
import FiCostEntry from './fi/FiCostEntry'
import PipelineBoard from './fi/PipelineBoard'

interface BillingDashboardProps {
  onEditBilling: (tenant: TenantSummary) => void
}

/**
 * Admin FI (Finanzas) tab — six sub-views:
 *   * Cockpit    — FiCockpit: FI overview (KPIs, income/cost, margin,
 *                  group/service splits, top clients, pipeline panel).
 *   * Facturas   — BillingWorkflow: current-period rows with invoice
 *                  lifecycle (pending / sent / invoiced / paid), "Send
 *                  to Finanzas" action and status mutations.
 *   * Analytics  — RevenueAnalytics (historical MRR/ARR charts).
 *   * Costos     — FiCostEntry: vendor cost rates + overhead cost entry.
 *   * Pipeline   — PipelineBoard: deals board (prospect → won/lost).
 *   * Fiscal     — tenant-level fiscal data (legal_name, tax_id, ...)
 *                  used by onEditBilling.
 *
 * Cockpit is the default landing view ("what's the state of FI right
 * now"); Facturas is the monthly operational task; the rest are
 * drill-down/edit surfaces.
 */
export default function BillingDashboard({ onEditBilling }: BillingDashboardProps) {
  const [tenants, setTenants] = useState<TenantSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [snapshotLoading, setSnapshotLoading] = useState(false)
  const [snapshotResult, setSnapshotResult] = useState<{ success: boolean; message: string } | null>(null)
  const [activeView, setActiveView] = useState<'cockpit' | 'workflow' | 'analytics' | 'costos' | 'pipeline' | 'fiscal'>('cockpit')

  useEffect(() => {
    fetchBillingData()
  }, [])

  const fetchBillingData = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.getGlobalMetrics()
      if (!result.success) throw new Error(result.error || 'Error al cargar datos')
      setTenants(result.data?.tenantSummaries || [])
    } catch (err) {
      console.error('Error fetching billing data:', err)
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  const activeTenants = tenants.filter(t => t.status === 'active')
  const totalUsers = activeTenants.reduce((sum, t) => sum + (t.billing?.billableUsers || 0), 0)
  const totalManagedRevenue = activeTenants.reduce((sum, t) => sum + (t.billing?.monthlyTotal || 0), 0)
  const tenantsWithBillingInfo = activeTenants.filter(t => t.billing?.hasBillingInfo).length
  const tenantsMissingInfo = activeTenants.length - tenantsWithBillingInfo

  const now = new Date()
  const billingPeriod = now.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })

  // Defaults to the current month, but Facturas passes its selected
  // period so past months can be (re)snapshotted — e.g. backfilling a
  // month the day-15 cron missed. overwrite:true replaces an existing
  // snapshot for that period.
  const handleCaptureSnapshot = async (year: number = now.getFullYear(), month: number = now.getMonth() + 1) => {
    setSnapshotLoading(true)
    setSnapshotResult(null)
    try {
      const result = await api.createBillingSnapshot({ year, month, overwrite: true })
      if (!result.success) throw new Error(result.error || 'Error al capturar snapshot')
      const data = result.data as { summary: { successCount: number; totalRevenue: number }; period: { label: string } }
      setSnapshotResult({
        success: true,
        message: `Snapshot ${data.period.label} capturado: ${data.summary.successCount} clientes, $${data.summary.totalRevenue.toLocaleString()} USD`,
      })
    } catch (err) {
      console.error('Error capturing snapshot:', err)
      setSnapshotResult({ success: false, message: err instanceof Error ? err.message : 'Error desconocido' })
    } finally {
      setSnapshotLoading(false)
      setTimeout(() => setSnapshotResult(null), 5000)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-orange"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchBillingData} className="ml-4 underline hover:no-underline">Reintentar</button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">FI · Finanzas</h2>
          <p className="text-gray-400 text-sm">
            Periodo actual: {billingPeriod} · Corte día 15
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle — Cockpit first (most common task) */}
          <div className="flex bg-gray-700 rounded-lg p-0.5">
            <button
              onClick={() => setActiveView('cockpit')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'cockpit' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Cockpit
            </button>
            <button
              onClick={() => setActiveView('workflow')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'workflow' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Facturas
            </button>
            <button
              onClick={() => setActiveView('analytics')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'analytics' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveView('costos')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'costos' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Costos
            </button>
            <button
              onClick={() => setActiveView('pipeline')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'pipeline' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setActiveView('fiscal')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === 'fiscal' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Fiscal
              {tenantsMissingInfo > 0 && (
                <span className="ml-1.5 bg-yellow-500 text-yellow-900 text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {tenantsMissingInfo}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Snapshot notification (shared across views) */}
      {snapshotResult && (
        <div className={`${
          snapshotResult.success ? 'bg-green-500/20 border-green-500 text-green-300' : 'bg-red-500/20 border-red-500 text-red-300'
        } border rounded-lg p-4 flex items-center gap-3`}>
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d={snapshotResult.success ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
          </svg>
          <span>{snapshotResult.message}</span>
        </div>
      )}

      {/* Cockpit View — FI overview: KPIs, income/cost, margin, pipeline */}
      {activeView === 'cockpit' && <FiCockpit />}

      {/* Costos View — vendor cost rates + overhead cost entry */}
      {activeView === 'costos' && <FiCostEntry />}

      {/* Pipeline View — deals board (prospect → won/lost) */}
      {activeView === 'pipeline' && <PipelineBoard />}

      {/* Workflow View — lifecycle of this month's billing */}
      {activeView === 'workflow' && (
        <BillingWorkflow
          onCaptureSnapshot={handleCaptureSnapshot}
          snapshotLoading={snapshotLoading}
        />
      )}

      {/* Revenue Analytics View */}
      {activeView === 'analytics' && (
        <RevenueAnalytics
          onCaptureSnapshot={handleCaptureSnapshot}
          snapshotLoading={snapshotLoading}
        />
      )}

      {/* Fiscal Data View */}
      {activeView === 'fiscal' && (
        <>
          {/* Warning if missing billing info */}
          {tenantsMissingInfo > 0 && (
            <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-yellow-300 font-medium">Datos de facturación incompletos</p>
                <p className="text-yellow-200/70 text-sm">
                  {tenantsMissingInfo} cliente(s) no tienen datos fiscales completos — estos NO pueden enviarse a Finanzas
                  hasta completar razón social, RUT/NIF y correo de facturación.
                </p>
              </div>
            </div>
          )}

          {/* Fiscal Data Table */}
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Datos Fiscales por Cliente</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-4 py-3 font-medium">Razón Social</th>
                    <th className="px-4 py-3 font-medium">RUT/NIF</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium text-right">Usuarios</th>
                    <th className="px-4 py-3 font-medium text-right">$/Usuario</th>
                    <th className="px-4 py-3 font-medium text-right">Total</th>
                    <th className="px-4 py-3 font-medium text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {activeTenants
                    .sort((a, b) => (b.billing?.monthlyTotal || 0) - (a.billing?.monthlyTotal || 0))
                    .map(tenant => (
                    <tr key={tenant.id} className="hover:bg-gray-700/30">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tenant.primaryColor }} />
                          <span className="text-white font-medium">{tenant.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {tenant.billing?.legalName ? (
                          <span className="text-gray-300">{tenant.billing.legalName}</span>
                        ) : (
                          <span className="text-yellow-400 text-sm">Sin configurar</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {tenant.billing?.taxId ? (
                          <span className="text-gray-300 font-mono text-sm">{tenant.billing.taxId}</span>
                        ) : (
                          <span className="text-yellow-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          tenant.plan === 'enterprise' ? 'bg-purple-500/20 text-purple-300' :
                          tenant.plan === 'standard' ? 'bg-blue-500/20 text-blue-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {tenant.planDisplayName || tenant.plan}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-white font-medium">{tenant.billing?.billableUsers || 0}</span>
                      </td>
                      <td className="px-4 py-4 text-right text-gray-400">
                        ${tenant.pricePerUser || 0}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-green-400 font-bold">
                          ${(tenant.billing?.monthlyTotal || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => onEditBilling(tenant)}
                          className="text-jarix-orange hover:text-jarix-pink transition-colors"
                          title="Editar datos de facturación"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-900/50 border-t-2 border-gray-600">
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-white font-bold">TOTAL</td>
                    <td className="px-4 py-4 text-right text-white font-bold">{totalUsers}</td>
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 text-right text-green-400 font-bold text-lg">
                      ${totalManagedRevenue.toLocaleString()}
                    </td>
                    <td className="px-4 py-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
